import {Component, Inject, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {CrudActions} from '@application/helper/grid-action-buttons-helper/crud-actions.interface';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {Schedule} from '@domain/calendar/schedule/schedule';
import {ScheduleException} from '@domain/calendar/schedule/schedule-exception';
import {SchedulePattern} from '@domain/calendar/schedule/schedule-pattern';
import {ScheduleUnavailableDay} from '@domain/calendar/schedule/schedule-unavailable-day';
import {IdName} from '@domain/id-name';
import {Permission} from '@domain/profile/permission.enum';
import {MatchingCriteria} from '@domain/utilities/tool/matching-criteria';
import {HttpSchedulesService} from '@infrastructure/http/calendar/schedule/http-schedules.service';
import {MACHINE_OVERVIEW, MachineOverview} from '@infrastructure/http/machine-overview/machine-overview';
import {MACHINES, Machines} from '@infrastructure/http/machine/machines.interface';
import {MachineOverviewList} from '@presentation/pages/machine-overview/machine-overview-list';
import {OverviewMachineGroup} from '@presentation/pages/machine-overview/overview-machine-group';
import {TextileService} from '@presentation/pages/textile-data/textile-data-overview/textile.service';
import {TextileDataType} from '@presentation/pages/textile-data/textile-data-type.enum';
import {MatchingCriteriaData} from '@presentation/pages/utilities/tool/add/matching-criteria/matching-criteria-data.interface';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  ColDefBuilderFactoryService,
  ContentSwitcherNavigationInput,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  CustomCellEditorComponent,
  CustomCellEditorParams,
  DateTimeFormat,
  DateTimePipe,
  DialogBuilderFactoryService,
  DialogType,
  FormValidationHelper,
  GridButtonClickedEvent,
  GridOptionsBuilder,
  NavigationHelperPrototypeService,
  noSpaceValidator,
  ObjectCellRendererComponent,
  SaveType,
  SuffixIconCellRendererComponent,
  SuffixIconCellRendererParams,
  TabsMenuItem,
  TranslateService,
  UuidUtils,
  Validation
} from '@vdw/angular-component-library';
import {CellEditingStoppedEvent, ColDef, EditableCallbackParams, GridApi, GridReadyEvent, ICellRendererParams, ITooltipParams} from 'ag-grid-community';
import {DateTime} from 'luxon';
import {finalize, forkJoin, map, Observable, takeUntil} from 'rxjs';
import {CalendarTimelineService} from '../../calendar-timeline.service';
import {PatternSelectionService} from '../../pattern/pattern-selection.service';
import {ScheduleOverviewService} from '../overview/schedule-overview.service';
import {AddScheduleTabs} from './add-schedule-tabs.enum';
import {PatternForm, ScheduleForm} from './schedule.form';

@Component({
  selector: 'app-add-schedule-page',
  templateUrl: './add-schedule-page.component.html',
  styleUrl: './add-schedule-page.component.scss',
  providers: [CalendarTimelineService]
})
export class AddSchedulePageComponent extends BaseComponent implements OnInit {
  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_SHIFT_SCHEDULE;
  public readonly TABS = AddScheduleTabs;
  public readonly SCHEDULE_TRANSLATION_KEY = NavigationUtils.getNavigationData(NavigationId.SCHEDULE_MODULE).navigationKey;
  public readonly PATTERN_TRANSLATION_KEY = NavigationUtils.getNavigationData(NavigationId.PATTERN_MODULE).navigationKey;
  public readonly SAVE_TYPE = SaveType;
  private readonly TODAY = DateTime.now().startOf('day');
  private readonly TOMORROW = this.TODAY.plus({days: 1});

  public addScheduleForm: ScheduleForm = new FormGroup({
    name: new FormControl(null, [Validators.required, noSpaceValidator], AsyncUniqueValidator.createValidator(this.schedules, null)),
    patterns: new FormArray([])
  });

  public menuItems: TabsMenuItem[] = [
    {value: AddScheduleTabs.GENERAL, translationKey: 'GENERAL.GENERAL', form: this.addScheduleForm},
    {value: AddScheduleTabs.SCHEDULE, translationKey: this.translate.instant(this.SCHEDULE_TRANSLATION_KEY, {count: 1})},
    {value: AddScheduleTabs.MATCHING_CRITERIA, translationKey: 'UTILITIES.TOOL.MATCHING_CRITERIA.MATCHING_CRITERIA'}
  ];

  public patternsConfig: CrudOverviewDataConfig<SchedulePattern>;
  public selectedMenuItem: TabsMenuItem = this.menuItems[0];
  public schedulePatterns: SchedulePattern[] = [];
  public unavailableDays: ScheduleUnavailableDay[] = [];
  public exceptions: ScheduleException[] = [];
  public matchingCriteria: MatchingCriteria[] = [];
  public matchingCriteriaData: MatchingCriteriaData = {machines: [], machineGroups: []};
  public isLoadingSchedule = false;
  private gridApi: GridApi;
  private scheduleId: number;

  private get patterns(): FormArray<PatternForm> {
    return this.addScheduleForm.controls.patterns;
  }

  public constructor(
    @Inject(MACHINES) private readonly machines: Machines,
    @Inject(MACHINE_OVERVIEW) private readonly machineOverview: MachineOverview,
    private readonly activatedRoute: ActivatedRoute,
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<SchedulePattern>,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly dateTimePipe: DateTimePipe,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper,
    private readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly patternSelectionService: PatternSelectionService,
    private readonly router: Router,
    private readonly schedules: HttpSchedulesService,
    private readonly textileService: TextileService,
    private readonly translate: TranslateService,
    private readonly scheduleOverviewService: ScheduleOverviewService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.addPattern();
    this.setPatternsConfig();

    forkJoin([this.machines.getAll(), this.machineOverview.getRootGroup()])
      .pipe(
        map(([machines, machineGroups]: [MachineOverviewList[], OverviewMachineGroup]) => [machines, machineGroups.groups]),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe(([machines, machineGroups]: [MachineOverviewList[], OverviewMachineGroup[]]) => (this.matchingCriteriaData = {machines, machineGroups}));

    const id = this.activatedRoute.snapshot.params.id;
    if (!AssertionUtils.isNullOrUndefined(id)) {
      this.isLoadingSchedule = true;
      this.schedules
        .getById(parseInt(id))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((schedule: Schedule) => {
          this.scheduleId = schedule.id;
          (schedule.patterns as SchedulePattern[]).forEach((pattern: SchedulePattern) => {
            if (!this.isEditingSchedule() && pattern.startDate < this.TOMORROW) {
              if (!AssertionUtils.isNullOrUndefined(pattern.endDate) && pattern.endDate < this.TOMORROW) {
                return;
              }
              pattern.startDate = this.TOMORROW;
            }
            this.schedulePatterns.push(pattern);
          });
          this.unavailableDays = this.isEditingSchedule()
            ? schedule.unavailableDays
            : schedule.unavailableDays.filter((unavailableDay: ScheduleUnavailableDay) => unavailableDay.date >= this.TOMORROW);
          this.exceptions = this.isEditingSchedule() ? schedule.exceptions : schedule.exceptions.filter((exception: ScheduleException) => exception.date >= this.TOMORROW);
          this.matchingCriteria = this.isEditingSchedule() ? schedule.matchingCriteria : [];
          this.setFormValues(schedule);
          this.isLoadingSchedule = false;
        });
    }
  }

  public onSelectedMenuItemChange(selectedMenuItem: TabsMenuItem): void {
    if (this.selectedMenuItem.value !== AddScheduleTabs.GENERAL && selectedMenuItem.value === AddScheduleTabs.GENERAL) {
      this.patternsConfig.rowData = (this.patterns.value as SchedulePattern[]).map((data: SchedulePattern, index: number) => {
        const patternControls = this.patterns.at(index).controls;
        data['errors'] = {pattern: patternControls.pattern.invalid, startDate: patternControls.startDate.invalid, endDate: patternControls.endDate.invalid};
        return data;
      });
      this.gridApi = null;
    } else if (this.selectedMenuItem.value === AddScheduleTabs.GENERAL && selectedMenuItem.value !== AddScheduleTabs.GENERAL && !this.isLoadingSchedule) {
      this.schedulePatterns = this.getCurrentPatterns();
    }
    this.selectedMenuItem = selectedMenuItem;
  }

  public canShowFormBasedOnTab(tab: AddScheduleTabs): boolean {
    return this.selectedMenuItem.value === tab;
  }

  public getSchedulePermission(): Permission {
    return NavigationUtils.getNavigationData(NavigationId.ADD_SCHEDULE).requiredPermission;
  }

  public duplicateSchedule(): void {
    this.navigationHelper.navigateWithRoute({route: NavigationUtils.getAbsolutePath(NavigationId.DUPLICATE_SCHEDULE).replace(':id', this.scheduleId.toString()), forgetCurrentEntry: true});
  }

  public deleteSchedule(): void {
    this.textileService.removeConfirmation(this.getCurrentSchedule(), TextileDataType.SHIFT_SCHEDULE, false, null, this.schedules);
  }

  public cancel(): void {
    this.router.navigateByUrl(NavigationUtils.getAbsolutePath(NavigationId.SCHEDULE));
  }

  public getLoadLink(): string {
    return ErrorHandlers.getLoadLink(NavigationUtils.getAbsolutePath(NavigationId.EDIT_SCHEDULE), this.addScheduleForm.controls.name);
  }

  public getLoadEntry(): ContentSwitcherNavigationInput {
    const editRoute = NavigationUtils.getNavigationRoute(NavigationId.EDIT_SCHEDULE);

    return {
      componentInfo: {
        component: editRoute.component,
        componentInputs: {pageType: PageTypeEnum.EDIT, itemId: this.addScheduleForm?.controls?.name?.errors?.entityId, canShowHeader: true}
      }
    };
  }

  public addPattern(pattern?: SchedulePattern): void {
    const id = pattern?.id ?? UuidUtils.generateV4Uuid();
    const newPattern = new FormGroup({
      id: new FormControl<number | string>(id),
      pattern: new FormControl(pattern?.pattern ?? null, Validators.required),
      startDate: new FormControl(pattern?.startDate ?? null, [Validators.required, this.overlappingPatternsValidator(id, 'startDate')]),
      endDate: new FormControl(pattern?.endDate ?? null, this.overlappingPatternsValidator(id, 'endDate'))
    });
    newPattern.statusChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => this.updatePatternsValidity());

    this.patterns.push(newPattern);
    this.gridApi?.applyTransaction({add: [this.patterns.at(this.patterns.length - 1).value]});

    if (AssertionUtils.isNullOrUndefined(pattern)) {
      const previousPattern = this.patterns.at(this.patterns.length - 2);
      this.gridApi?.redrawRows({rowNodes: [this.gridApi?.getRowNode(previousPattern.value.id.toString())]});
      this.updatePatternValidity(previousPattern);
    }
  }

  public saveSchedule(saveType: SaveType): void {
    const isValid = new FormValidationHelper().checkForms([this.addScheduleForm], document);
    if (this.canShowFormBasedOnTab(AddScheduleTabs.GENERAL)) {
      this.updatePatternsValidity();
    }

    if (isValid) {
      const scheduleToSave = this.getCurrentSchedule();
      this.saving = true;

      const request: Observable<void | number> = this.isEditingSchedule() ? this.schedules.update(scheduleToSave) : this.schedules.save(scheduleToSave);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving())).subscribe({
        next: (id: number) => {
          if (saveType === SaveType.SAVE) {
            this.addScheduleForm.controls.name.setAsyncValidators(AsyncUniqueValidator.createValidator(this.schedules, scheduleToSave.name));
          }
          this.textileService.navigateAndShowToast(saveType, TextileDataType.SHIFT_SCHEDULE, this.SCHEDULE_TRANSLATION_KEY, this.isEditingSchedule(), scheduleToSave.name, id);
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.isEditingSchedule() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  public isEditingSchedule(): boolean {
    return this.activatedRoute.snapshot.routeConfig.path === NavigationUtils.getRoute(NavigationId.EDIT_SCHEDULE);
  }

  protected openCustomSettings(): void {
    this.scheduleOverviewService.openCustomSettings();
  }

  private setPatternsConfig(): void {
    this.patternsConfig = this.builderFactoryService
      .getBuilder()
      .withDefaultButtons({
        crudOverviewButtonConfigs: this.gridActionButtonsHelper.getActionButtonsForGridSelection({
          requiredPermissionToEdit: this.getSchedulePermission(),
          withDelete: (grid: GridButtonClickedEvent) => this.deletePattern(grid),
          canBeDeleted: (data: SchedulePattern) => this.gridApi?.getDisplayedRowCount() > 1 && (AssertionUtils.isNullOrUndefined(data.startDate) || data.startDate > this.TODAY)
        } as CrudActions)
      })
      .withGridOptions(this.getPatternColumnDefs(), GridIdentifier.SCHEDULE_PATTERN, this.patterns.value as SchedulePattern[], (builder: GridOptionsBuilder) =>
        builder
          .withOnGridReady((event: GridReadyEvent) => (this.gridApi = event.api))
          .withOnCellEditingStopped((event: CellEditingStoppedEvent) => {
            if (
              event.column.getColId() === 'pattern' ||
              AssertionUtils.isNullOrUndefined(event.oldValue) ||
              AssertionUtils.isNullOrUndefined(event.newValue) ||
              !event.oldValue.hasSame(event.newValue, 'day')
            ) {
              const patternForm = this.getPatternFormById(event.data.id);
              this.removeExceptionsInDateRange(
                event.column.getColId() === 'startDate' ? this.getMinOrMaxDateTime(event.oldValue, event.newValue, 'min') : patternForm.value.startDate,
                event.column.getColId() === 'endDate' ? this.getMinOrMaxDateTime(event.oldValue, event.newValue, 'max') : patternForm.value.endDate,
                this.isLastRow(event.rowIndex)
              );
            }
            setTimeout(() => {
              if (!event.api.isDestroyed() && event.api.getEditingCells()?.length === 0) {
                event.api.onSortChanged();
                this.updateLastRowEndDate();
                event.api.redrawRows();
                this.updatePatternsValidity();
              }
            }, 200);
          })
          .withIsRowSelectable(() => false, false)
          .build()
      )
      .build();
  }

  private deletePattern({rowNode}: GridButtonClickedEvent): void {
    const isLastRowDeleted = this.isLastRow(rowNode.rowIndex);
    const patternFormToBeDeleted = this.getPatternFormById(rowNode.data.id);
    this.removeExceptionsInDateRange(patternFormToBeDeleted.value.startDate, patternFormToBeDeleted.value.endDate, isLastRowDeleted);
    this.patterns.removeAt(this.patterns.controls.findIndex((patternForm: PatternForm) => patternForm === patternFormToBeDeleted));
    this.gridApi.applyTransaction({remove: [rowNode.data]});
    if (isLastRowDeleted) {
      this.updateLastRowEndDate();
      this.gridApi.redrawRows({rowNodes: [this.gridApi.getDisplayedRowAtIndex(this.gridApi.getLastDisplayedRowIndex())]});
    }
    this.updatePatternsValidity();
  }

  private updateLastRowEndDate(): void {
    const lastRowNode = this.gridApi.getDisplayedRowAtIndex(this.gridApi.getLastDisplayedRowIndex());
    this.getPatternFormById(lastRowNode.data.id).controls.endDate.setValue(null);
    lastRowNode.data.endDate = null;
  }

  private removeExceptionsInDateRange(startDate: DateTime, endDate: DateTime, isLastRow: boolean): void {
    if (AssertionUtils.isNullOrUndefined(startDate) || (!isLastRow && AssertionUtils.isNullOrUndefined(endDate))) {
      return;
    }
    this.exceptions = this.exceptions.filter((exception: ScheduleException) => exception.date < startDate || (!isLastRow && exception.date > endDate));
  }

  private getMinOrMaxDateTime(oldValue: DateTime, newValue: DateTime, minOrMax: 'min' | 'max'): DateTime {
    if (AssertionUtils.isNullOrUndefined(oldValue)) {
      return newValue;
    }
    if (AssertionUtils.isNullOrUndefined(newValue)) {
      return oldValue;
    }
    return DateTime[minOrMax](oldValue, newValue);
  }

  private getPatternColumnDefs(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName(this.PATTERN_TRANSLATION_KEY)
        .withColIdAndField('pattern')
        .withCellRenderer(ObjectCellRendererComponent)
        .withEditable(
          (params: EditableCallbackParams) => AssertionUtils.isNullOrUndefined(params.data.startDate) || params.data.startDate >= this.TOMORROW,
          CustomCellEditorComponent,
          (params: EditableCallbackParams): CustomCellEditorParams =>
            ({selectionService: this.patternSelectionService, formControl: this.getPatternFormById(params.data.id).controls.pattern}) as CustomCellEditorParams
        )
        .withOnCellClicked()
        .withoutFilter()
        .withSuppressHeaderMenuButton()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('GENERAL.DATETIME.START_DATE')
        .withColIdAndField('startDate')
        .withCellRenderer(SuffixIconCellRendererComponent, {icon: 'solid-calendar-today', objectKey: 'ANGULAR_COMPONENT_LIBRARY.CUSTOM_CELL_EDITOR.DATE'} as SuffixIconCellRendererParams)
        .withEditable(
          (params: EditableCallbackParams) => AssertionUtils.isNullOrUndefined(params.data.startDate) || params.data.startDate >= this.TOMORROW,
          CustomCellEditorComponent,
          (params: EditableCallbackParams) => ({
            number: false,
            date: true,
            min: this.TOMORROW,
            max: params.data.endDate,
            formControl: this.getPatternFormById(params.data.id).controls.startDate,
            validations: [
              new Validation(null, 'overlappingPatterns', this.translate.instant('CALENDAR.ERROR.OBJECT_CANT_OVERLAP', {object: this.translate.instant(this.PATTERN_TRANSLATION_KEY, {count: 2})}))
            ]
          }),
          true
        )
        .withTooltipValueGetter((params: ITooltipParams) => this.dateTimePipe.transform(params.data.startDate, DateTimeFormat.DATE_SHORT))
        .withOnCellClicked()
        .withComparator((valueA: DateTime, valueB: DateTime) => {
          if (AssertionUtils.isNullOrUndefined(valueA) && AssertionUtils.isNullOrUndefined(valueB)) {
            return 0;
          }
          if (AssertionUtils.isNullOrUndefined(valueA)) {
            return 1;
          }
          if (AssertionUtils.isNullOrUndefined(valueB)) {
            return -1;
          }
          return valueA.diff(valueB).milliseconds;
        })
        .withSort('asc')
        .withoutFilter()
        .withSuppressHeaderMenuButton()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('GENERAL.DATETIME.END_DATE')
        .withColIdAndField('endDate')
        .withCellRenderer(
          SuffixIconCellRendererComponent,
          (params: ICellRendererParams) =>
            (this.isLastRow(params.node.rowIndex)
              ? {placeholder: this.translate.instant('GENERAL.INFINITY')}
              : {icon: 'solid-calendar-today', objectKey: 'ANGULAR_COMPONENT_LIBRARY.CUSTOM_CELL_EDITOR.DATE'}) as SuffixIconCellRendererParams
        )
        .withEditable(
          (params: EditableCallbackParams) => !this.isLastRow(params.node.rowIndex) && (AssertionUtils.isNullOrUndefined(params.data.endDate) || params.data.endDate >= this.TODAY),
          CustomCellEditorComponent,
          (params: EditableCallbackParams) => ({
            number: false,
            date: true,
            min: params.data.startDate ? DateTime.max(params.data.startDate, this.TODAY) : this.TODAY,
            formControl: this.getPatternFormById(params.data.id).controls.endDate,
            validations: [
              new Validation(null, 'overlappingPatterns', this.translate.instant('CALENDAR.ERROR.OBJECT_CANT_OVERLAP', {object: this.translate.instant(this.PATTERN_TRANSLATION_KEY, {count: 2})}))
            ]
          }),
          true
        )
        .withTooltipValueGetter((params: ITooltipParams) => this.dateTimePipe.transform(params.data.endDate, DateTimeFormat.DATE_SHORT))
        .withOnCellClicked()
        .withoutFilter()
        .withSuppressHeaderMenuButton()
        .build()
    ];
  }

  private isLastRow(rowIndex: number): boolean {
    return rowIndex === this.patterns.length - 1;
  }

  private getPatternFormById(id: number | string): PatternForm {
    return this.patterns.at(this.patterns.value.findIndex((data: SchedulePattern) => data.id === id));
  }

  private overlappingPatternsValidator(id: number | string, formControlName: 'startDate' | 'endDate'): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (AssertionUtils.isNullOrUndefined(this.gridApi?.getRenderedNodes())) {
        return null;
      }

      const isLastRow = id === this.gridApi.getDisplayedRowAtIndex(this.gridApi.getLastDisplayedRowIndex()).data.id;
      if (AssertionUtils.isNullOrUndefined(control.value)) {
        return formControlName === 'startDate' || isLastRow ? null : {required: true};
      }

      const newStart = formControlName === 'startDate' ? control.value : control.parent?.value?.startDate;
      const newEnd = formControlName === 'endDate' ? control.value : control.parent?.value?.endDate;

      const overlappingPatterns = this.patterns.value
        .filter((pattern: SchedulePattern) => pattern.id !== id)
        .some((pattern: SchedulePattern) => {
          if (pattern.startDate?.hasSame(control.value, 'day') || pattern.endDate?.hasSame(control.value, 'day')) {
            return true;
          }

          const isComparingWithLastRow = pattern.id === this.gridApi.getDisplayedRowAtIndex(this.gridApi.getLastDisplayedRowIndex()).data.id;
          if (AssertionUtils.isNullOrUndefined(pattern.startDate) || (!isComparingWithLastRow && AssertionUtils.isNullOrUndefined(pattern.endDate))) {
            return false;
          }

          return (
            (isLastRow && formControlName === 'startDate' && control.value > pattern.startDate && control.value < pattern.endDate) ||
            (isComparingWithLastRow && formControlName === 'endDate' && control.value > pattern.startDate) ||
            (!AssertionUtils.isNullOrUndefined(newStart) && !AssertionUtils.isNullOrUndefined(newEnd) && newStart < pattern.endDate && newEnd > pattern.startDate)
          );
        });

      return overlappingPatterns ? {overlappingPatterns: true} : null;
    };
  }

  private updatePatternsValidity(): void {
    if (!AssertionUtils.isNullOrUndefined(this.gridApi)) {
      setTimeout(() =>
        this.patterns.controls.forEach((patternForm: PatternForm) => {
          this.updatePatternValidity(patternForm);
        })
      );
    }
  }

  private updatePatternValidity(patternForm: PatternForm): void {
    patternForm.controls.pattern.updateValueAndValidity({emitEvent: false});
    patternForm.controls.startDate.updateValueAndValidity({emitEvent: false});
    patternForm.controls.endDate.updateValueAndValidity({emitEvent: false});

    if (!AssertionUtils.isNullOrUndefined(this.gridApi?.getRenderedNodes())) {
      const rowNode = this.gridApi.getRowNode(patternForm.value.id.toString());
      rowNode.data.errors = {
        pattern: patternForm.controls.pattern.touched && patternForm.controls.pattern.invalid,
        startDate: patternForm.controls.startDate.touched && patternForm.controls.startDate.invalid,
        endDate: patternForm.controls.endDate.touched && patternForm.controls.endDate.invalid
      };
      this.gridApi.refreshCells({rowNodes: [rowNode]});
    }
  }

  private setFormValues(schedule: Schedule): void {
    const name = this.isEditingSchedule() ? schedule.name : null;
    this.addScheduleForm.controls.name.reset(name);
    this.addScheduleForm.controls.name.setAsyncValidators(AsyncUniqueValidator.createValidator(this.schedules, name));
    this.patterns.clear();
    this.schedulePatterns.forEach((pattern: SchedulePattern) => this.addPattern(pattern));

    this.patternsConfig.rowData = this.patterns.value as SchedulePattern[];
    this.gridApi?.redrawRows();
    this.updatePatternsValidity();
  }

  private getCurrentPatterns(): SchedulePattern[] {
    if (this.patterns.invalid) {
      return [];
    }
    return this.patterns.controls.map(
      (pattern: PatternForm) => new SchedulePattern(pattern.value.id, new IdName(pattern.value.pattern.id, pattern.value.pattern.name), pattern.value.startDate, pattern.value.endDate)
    );
  }

  private getCurrentSchedule(): Schedule {
    return new Schedule(this.isEditingSchedule() ? this.scheduleId : null, this.addScheduleForm.value.name, this.getCurrentPatterns(), this.unavailableDays, this.exceptions, this.matchingCriteria);
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {
        object: this.translate.instant(this.SCHEDULE_TRANSLATION_KEY, {count: 1})
      }),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }
}
